import {
  addMonths,
  eachDayOfInterval,
  endOfMonth,
  isSameDay,
  isSameMonth,
  startOfDay,
  startOfMonth,
  subMonths,
} from 'date-fns'
import { formatInTimeZone } from 'date-fns-tz'
import type { AppointmentTypeDto } from '@shared/core/appointment-types'
import { TOO_MANY_ATTEMPTS_MESSAGE } from '@shared/core/bookings'
import type { EffectiveCancellationPolicyDto } from '@shared/core/cancellation-policy'
import { isTooManyRequestsError } from 'src/modules/booking/utils/too-many-requests'
import type {
  AppointmentTypeForOptions,
  AppointmentTypeOption,
  CalendarDayView,
  ContinuePayload,
  FirstAvailableSummary,
  SlotDisplay,
  SlotForValidation,
  TimezoneOption,
  TimezoneSources,
} from './booking-date-and-time-step.interface'

export function resolveInitialTimezone(options: TimezoneOption[], sources: TimezoneSources): string {
  const ordered = [sources.saved, sources.initial, sources.browser].filter(
    (v): v is string => typeof v === 'string' && v.length > 0,
  )
  for (const value of ordered) {
    if (options.some((o) => o.value === value)) return value
  }
  return options[0]?.value ?? 'UTC'
}

export function resolveInitialAppointmentType(
  appointmentTypes: AppointmentTypeForOptions[],
  initialId?: string,
): string {
  if (initialId) return initialId
  if (appointmentTypes.length === 1) return appointmentTypes[0].id
  return ''
}

/** The reschedule-mode date step shows no policy in v1. */
export function resolveSelectedCancellationPolicy(
  appointmentTypes: Pick<AppointmentTypeDto, 'id' | 'effective_cancellation_policy'>[],
  selectedTypeId: string,
  isRescheduling: boolean,
): EffectiveCancellationPolicyDto | null {
  if (isRescheduling || !selectedTypeId) return null
  const policy = appointmentTypes.find((type) => type.id === selectedTypeId)?.effective_cancellation_policy
  return policy?.text?.trim() ? policy : null
}

export function buildAppointmentTypeOptions(appointmentTypes: AppointmentTypeForOptions[]): AppointmentTypeOption[] {
  const mapped = appointmentTypes.map((type) => ({
    value: type.id,
    label: `${type.name}, ${type.duration_minutes} min`,
  }))
  return mapped
}

export function canNavigateToPrevMonth(currentMonth: Date, today: Date): boolean {
  const prevMonth = subMonths(currentMonth, 1)
  const prevMonthEnd = startOfDay(endOfMonth(prevMonth))
  return prevMonthEnd >= today
}

/**
 * The availability response is the only authority. It is already grandfathered
 * when the request carried a reschedule code, so the picker takes it verbatim
 * and computes no bound of its own. Null means no window.
 */
export function resolveLastBookableDay(bookingWindowEndDate?: string | null): Date | null {
  if (!bookingWindowEndDate) return null
  const parsed = new Date(bookingWindowEndDate + 'T00:00:00')
  return isNaN(parsed.getTime()) ? null : startOfDay(parsed)
}

export function canNavigateToNextMonth(currentMonth: Date, lastBookableDay: Date | null): boolean {
  if (!lastBookableDay) return true
  return startOfDay(startOfMonth(addMonths(currentMonth, 1))) <= lastBookableDay
}

export function buildCalendarDayViews(
  currentMonth: Date,
  selectedDate: Date | null,
  today: Date,
  lastBookableDay: Date | null = null,
): (CalendarDayView | { key: string; date: null })[] {
  const monthStart = startOfMonth(currentMonth)
  const monthEnd = endOfMonth(currentMonth)
  const days = eachDayOfInterval({ start: monthStart, end: monthEnd })
  const firstDayOfWeek = monthStart.getDay()
  const emptyCells = Array(firstDayOfWeek).fill(null) as null[]
  const emptyViews = emptyCells.map((_, i) => ({ key: `empty-${i}`, date: null as null }))
  const dayViews: CalendarDayView[] = days.map((date) => {
    const dayStart = startOfDay(date)
    return {
      key: date.toISOString(),
      date,
      isPast: dayStart < today,
      isOutsideWindow: lastBookableDay !== null && dayStart > lastBookableDay,
      isSelected: selectedDate !== null && isSameDay(date, selectedDate),
      isCurrentMonth: isSameMonth(date, currentMonth),
    }
  })
  return [...emptyViews, ...dayViews]
}

export function formatSlotsForDisplay(slots: SlotForValidation[], timezone: string): SlotDisplay[] {
  return slots
    .map((slot) => {
      const startTime = new Date(slot.start_time_utc)
      const time = formatInTimeZone(startTime, timezone, 'h:mm a')
      return {
        time,
        startTimeUtc: slot.start_time_utc,
        endTimeUtc: slot.end_time_utc,
        isFree: slot.is_free,
      }
    })
    .sort((a, b) => a.startTimeUtc.localeCompare(b.startTimeUtc))
}

export function parseFirstAvailableDate(summary: FirstAvailableSummary | undefined): Date | null {
  const dateStr = summary?.date
  if (!dateStr) return null
  const date = new Date(dateStr + 'T00:00:00')
  return isNaN(date.getTime()) ? null : date
}

export function getContinueButtonLabel(isRescheduling: boolean): string {
  return isRescheduling ? 'Reschedule' : 'Continue to Details →'
}

export const CALENDAR_REFUSED_TITLE = 'Online booking is unavailable'

export function calendarRefusedMessage(isRescheduling: boolean): string {
  const action = isRescheduling ? 'reschedule' : 'book'
  return `This business's calendar can't be reached, so online booking is paused. Please contact the business directly to ${action}.`
}

export const FIRST_AVAILABLE_DATE_ERROR_MESSAGE =
  'We could not work out the next free date. This is usually temporary, so please try again in a moment.'
export const DAY_SLOTS_ERROR_MESSAGE =
  'We could not load the times for this date. This is usually temporary, so please try again in a moment.'

export function availabilityErrorMessage(error: unknown, fallbackMessage: string): string {
  return isTooManyRequestsError(error) ? TOO_MANY_ATTEMPTS_MESSAGE : fallbackMessage
}

export function shouldShowAppointmentTypeSelector(
  isRescheduling: boolean,
  allowChangingAppointmentType?: boolean,
): boolean {
  return isRescheduling ? (allowChangingAppointmentType ?? false) : true
}

export type ValidateContinueResult = { ok: true; payload: ContinuePayload } | { ok: false; error: string }

export function validateContinueAndBuildPayload(
  slots: SlotForValidation[],
  selectedTime: string,
  timezone: string,
  appointmentType: string,
  selectedDate: Date,
): ValidateContinueResult {
  const selectedSlot = slots.find((slot) => {
    const startTime = new Date(slot.start_time_utc)
    const formatted = formatInTimeZone(startTime, timezone, 'h:mm a')
    return formatted === selectedTime
  })
  if (!selectedSlot) {
    return { ok: false, error: 'Selected time slot was not found. Please select another time.' }
  }
  if (!selectedSlot.is_free) {
    return { ok: false, error: 'This time slot is no longer available. Please select another time.' }
  }
  return {
    ok: true,
    payload: {
      appointmentType,
      selectedDate,
      selectedTime,
      timezone,
      start_time_utc: selectedSlot.start_time_utc,
      end_time_utc: selectedSlot.end_time_utc,
    },
  }
}
